<template>
  <div class="home">

    <!-- Hero Section with Terminal Style -->
    <div class="hero-section">
      <div class="hero-content">
        <div class="terminal-window">
          <div class="terminal-header">
            <div class="terminal-dots">
              <span class="terminal-dot red"></span>
              <span class="terminal-dot yellow"></span>
              <span class="terminal-dot green"></span>
            </div>
            <span class="terminal-title">welcome.sh</span>
          </div>
          <div class="terminal-body">
            <p class="terminal-line">
              <span class="prompt">$</span> echo $GREETING
            </p>
            <h1 class="greeting-output">{{ greetingMessage }}</h1>
            <p class="terminal-line">
              <span class="prompt">$</span> whoami
            </p>
            <p class="output-text">Jonathan Back <span class="cursor">_</span></p>
            <p class="terminal-line">
              <span class="prompt">$</span> cat /etc/motd
            </p>
            <p class="output-text dim">Full-stack developer crafting digital experiences</p>
            <p class="output-text dim">from the shores of Finland</p>
          </div>
        </div>
        <div class="scroll-indicator">
          <span>scroll</span>
          <div class="scroll-arrow"></div>
        </div>
      </div>
      <div class="hero-bg"></div>
    </div>


    <div class="container py-5">


      <!-- About Me Section -->
      <section class="about-section">
        <h2 class="section-title" data-bloop-id="section_about" data-bloop-kind="section">
          <span class="title-prefix">01.</span> about_me
        </h2>
        <div class="row align-items-center">
          <!-- Image -->
          <div class="col-lg-5 order-lg-2 mb-4 mb-lg-0">
            <div class="image-frame">
              <img src="../assets/images/portrait_Jonathan.jpeg" alt="Portrait" class="portrait-img">
              <div class="image-border"></div>
            </div>
          </div>
          <!-- Info -->
          <div class="col-lg-7 order-lg-1">
            <div class="terminal-card">
              <div class="terminal-card-header">
                <div class="terminal-dots">
                  <span class="terminal-dot red"></span>
                  <span class="terminal-dot yellow"></span>
                  <span class="terminal-dot green"></span>
                </div>
                <span class="terminal-card-title">~/info.json</span>
              </div>
              <div class="terminal-card-body">
                <div class="info-grid">
                  <div class="info-item" data-bloop-id="info_name" data-bloop-kind="info">
                    <span class="info-key">"name":</span>
                    <span class="info-value">"Jonathan Back"</span>
                  </div>
                  <div class="info-item" data-bloop-id="info_age" data-bloop-kind="info">
                    <span class="info-key">"age":</span>
                    <span class="info-value">{{ dynamicAge }}</span>
                  </div>
                  <div class="info-item" data-bloop-id="info_location" data-bloop-kind="info">
                    <span class="info-key">"location":</span>
                    <span class="info-value">"Vörå, Finland"</span>
                  </div>
                  <div class="info-item" data-bloop-id="info_education" data-bloop-kind="info">
                    <span class="info-key">"education":</span>
                    <span class="info-value">"BSc IT (Novia UAS)"</span>
                  </div>
                  <div class="info-item" data-bloop-id="info_status" data-bloop-kind="info">
                    <span class="info-key">"status":</span>
                    <span class="info-value">"System Developer @ B2 Impact & Studying MSc Computer Engineering (Åbo Akademi)"</span>
                  </div>
                  <div class="info-item">
                    <span class="info-key">"interests":</span>
                    <span class="info-value">[<template v-for="(hobby, index) in hobbies" :key="hobby.bloopId"><span
                      :data-bloop-id="hobby.bloopId"
                      data-bloop-kind="hobby"
                      :data-bloop-label="hobby.name"
                    >"{{ hobby.name }}"</span><template v-if="index < hobbies.length - 1">, </template></template>]</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      <!-- Skills Section -->
      <section class="skills-section">
        <h2 class="section-title" data-bloop-id="section_skills" data-bloop-kind="section">
          <span class="title-prefix">02.</span> skills.config
        </h2>
        <p class="section-subtitle">I'm a Junior Developer, constantly learning and evolving. Here's what I'm comfortable with using:<br><small>Because how do you know 55% of javascript, what is the remaining 45%?</small></p>
        
        <div class="row">
          <!-- Programming Skills -->
          <div class="col-lg-6 mb-4">
            <div class="terminal-card">
              <div class="terminal-card-header">
                <div class="terminal-dots">
                  <span class="terminal-dot red"></span>
                  <span class="terminal-dot yellow"></span>
                  <span class="terminal-dot green"></span>
                </div>
                <span class="terminal-card-title">programming_languages.sh</span>
              </div>
              <div class="terminal-card-body">
                <div v-for="skill in skills" :key="skill.name" class="skill-item" :data-bloop-id="skill.bloopId" data-bloop-kind="skill">
                  <div class="skill-header">
                    <span class="skill-name">{{ skill.name }}</span>
                    <span class="skill-level">{{ skill.level }}%</span>
                  </div>
                  <div class="skill-bar">
                    <div class="skill-progress" :style="{ width: skill.level + '%' }"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Languages & Fun Skills -->
          <div class="col-lg-6 mb-4">
            <div class="terminal-card mb-4">
              <div class="terminal-card-header">
                <div class="terminal-dots">
                  <span class="terminal-dot red"></span>
                  <span class="terminal-dot yellow"></span>
                  <span class="terminal-dot green"></span>
                </div>
                <span class="terminal-card-title">languages.env</span>
              </div>
              <div class="terminal-card-body">
                <div v-for="language in languages" :key="language.name" class="language-item" :data-bloop-id="language.bloopId" data-bloop-kind="language">
                  <span class="lang-name">{{ language.name }}</span>
                  <StarRating :rating="language.level" />
                </div>
              </div>
            </div>

            <div class="terminal-card">
              <div class="terminal-card-header">
                <div class="terminal-dots">
                  <span class="terminal-dot red"></span>
                  <span class="terminal-dot yellow"></span>
                  <span class="terminal-dot green"></span>
                </div>
                <span class="terminal-card-title">soft_skills.txt</span>
              </div>
              <div class="terminal-card-body">
                <div v-for="skill in otherSkills" :key="skill.name" class="skill-item" :data-bloop-id="skill.bloopId" data-bloop-kind="soft_skill">
                  <div class="skill-header">
                    <span class="skill-name">{{ skill.name }}</span>
                    <span class="skill-level">{{ skill.level }}%</span>
                  </div>
                  <div class="skill-bar fun">
                    <div class="skill-progress" :style="{ width: skill.level + '%' }"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      <!-- Technologies Section -->
      <section class="tech-section">
        <h2 class="section-title" data-bloop-id="section_tech" data-bloop-kind="section">
          <span class="title-prefix">03.</span> tech_stack
        </h2>
        <div class="terminal-card">
          <div class="terminal-card-header">
            <div class="terminal-dots">
              <span class="terminal-dot red"></span>
              <span class="terminal-dot yellow"></span>
              <span class="terminal-dot green"></span>
            </div>
            <span class="terminal-card-title">$ npm list --depth=0</span>
          </div>
          <div class="terminal-card-body">
            <div class="tech-grid">
              <TooltipComponent v-for="tech in technologies" :key="tech.name" :text="tech.name">
                <div class="tech-item" :data-bloop-id="tech.bloopId" data-bloop-kind="technology" :data-bloop-tooltip="tech.name">
                  <img :src="tech.icon" :alt="tech.name" class="tech-icon" />
                  <span class="tech-name">{{ tech.name }}</span>
                </div>
              </TooltipComponent>
            </div>
          </div>
        </div>
      </section>


      <!-- Git Section -->
      <section class="git-section">
        <h2 class="section-title" data-bloop-id="section_git" data-bloop-kind="section">
          <span class="title-prefix">04.</span> git_history
        </h2>
        <div class="terminal-card">
          <div class="terminal-card-header">
            <div class="terminal-dots">
              <span class="terminal-dot red"></span>
              <span class="terminal-dot yellow"></span>
              <span class="terminal-dot green"></span>
            </div>
            <span class="terminal-card-title">$ git log --oneline --graph</span>
          </div>
          <div class="terminal-card-body text-center">
            <p class="git-comment"># I write code*</p>
            <img src="../assets/images/git.png" alt="Git History" class="git-image">
            <p class="git-footnote">* This is an approximation and may actually only be me trying to center the image above</p>
          </div>
        </div>
      </section>

    </div>
  </div>
</template>

<script>
import StarRating from '../components/StarRating.vue';
import TooltipComponent from '../components/TooltipComponent.vue';

export default {
  name: 'HomeView',
  components: {
    StarRating,
    TooltipComponent
  },
  data() {
    return {
      greetingMessage: '',
      time: null,
      dynamicAge: null,
      hobbies: [
        { name: 'Tech', bloopId: 'hobby_tech' },
        { name: 'FPV Drones', bloopId: 'hobby_fpv' },
        { name: 'Fishing', bloopId: 'hobby_fishing' },
        { name: 'Nature', bloopId: 'hobby_nature' },
        { name: 'Cycling', bloopId: 'hobby_cycling' },
        { name: 'Gym', bloopId: 'hobby_gym' },
      ],
      skills: [
        { name: 'JavaScript', level: 55, bloopId: 'skill_javascript' },
        { name: 'Python', level: 70, bloopId: 'skill_python' },
        { name: 'SQL', level: 60, bloopId: 'skill_sql' },
        { name: 'C#', level: 65, bloopId: 'skill_csharp' },
      ],
      languages: [
        { name: 'English', level: 4, bloopId: 'lang_english' },
        { name: 'Swedish', level: 5, bloopId: 'lang_swedish' },
        { name: 'Finnish', level: 4, bloopId: 'lang_finnish' },
      ],
      otherSkills: [
        { name: 'Giphy', level: 80, bloopId: 'soft_giphy' },
        { name: 'Merging Pull Requests', level: 90, bloopId: 'soft_merging' },
        { name: 'Being Tall', level: 100, bloopId: 'soft_tall' },
      ],
      technologies: [
        { name: "Azure", bloopId: "tech_azure", icon: require('@/assets/icons/azure.png') },
        { name: "Vue", bloopId: "tech_vue", icon: require('@/assets/icons/vue.png') },
        { name: "Bootstrap", bloopId: "tech_bootstrap", icon: require('@/assets/icons/bootstrap.png') },
        { name: "Node.js", bloopId: "tech_nodejs", icon: require('@/assets/icons/nodejs.png') },
        { name: ".Net", bloopId: "tech_dotnet", icon: require('@/assets/icons/dotnet.png') },
        { name: "Capacitor.js", bloopId: "tech_capacitor", icon: require('@/assets/icons/capacitor.png') },
        { name: "React", bloopId: "tech_react", icon: require('@/assets/icons/react.png') },
      ],
    }
  },
  created() {
    this.setGreeting();
  },
  methods: {
    setGreeting() {
      this.time = new Date()
      const hour = new Date().getHours();
      if (hour < 12) {
        this.greetingMessage = 'Good Morning';
      } else if (hour < 18) {
        this.greetingMessage = 'Good Afternoon';
      } else {
        this.greetingMessage = 'Good Evening';
      }
    },
    calculateAge() {
      const birthDate = new Date('1999-04-13');
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        this.dynamicAge = age - 1;
      } else {
        this.dynamicAge = age;
      }
    }
  },
  mounted() {
    this.calculateAge();
  }
}
</script>

<style scoped>
/* Hero Section */
.hero-section {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.hero-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background:
    radial-gradient(ellipse at 20% 80%, rgba(57, 255, 20, 0.1) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 20%, rgba(0, 217, 255, 0.08) 0%, transparent 50%),
    var(--terminal-bg);
  z-index: -1;
}

.hero-content {
  text-align: center;
  z-index: 1;
}

.terminal-window {
  background: var(--terminal-card-bg);
  border: 1px solid var(--terminal-border);
  border-radius: 12px;
  max-width: 600px;
  margin: 0 auto;
  text-align: left;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(57, 255, 20, 0.1);
}

.terminal-header {
  background: var(--terminal-bg);
  padding: 12px 16px;
  border-bottom: 1px solid var(--terminal-border);
  border-radius: 12px 12px 0 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.terminal-dots {
  display: flex;
  gap: 8px;
}

.terminal-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.terminal-dot.red { background: #ff5f56; }
.terminal-dot.yellow { background: #ffbd2e; }
.terminal-dot.green { background: #27c93f; }

.terminal-title {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.85rem;
  color: var(--terminal-text-dim);
}

.terminal-body {
  padding: 24px;
  font-family: 'JetBrains Mono', monospace;
}

.terminal-line {
  color: var(--terminal-text-dim);
  margin-bottom: 8px;
  font-size: 0.9rem;
}

.prompt {
  color: var(--terminal-green);
  margin-right: 8px;
}

.greeting-output {
  color: var(--terminal-cyan);
  font-size: 2rem;
  margin-bottom: 20px;
  animation: glow 3s ease-in-out infinite;
}

@keyframes glow {
  0%, 100% { text-shadow: 0 0 5px var(--terminal-cyan), 0 0 10px var(--terminal-cyan); }
  50% { text-shadow: 0 0 10px var(--terminal-cyan), 0 0 20px var(--terminal-cyan), 0 0 30px var(--terminal-cyan); }
}

.output-text {
  color: var(--terminal-text);
  margin-bottom: 15px;
  font-size: 1.1rem;
}

.output-text.dim {
  color: var(--terminal-text-dim);
  font-size: 0.95rem;
}

.cursor {
  animation: blink 1s step-end infinite;
  color: var(--terminal-green);
}

@keyframes blink {
  0%, 50% { opacity: 1; }
  51%, 100% { opacity: 0; }
}

.scroll-indicator {
  margin-top: 60px;
  color: var(--terminal-text-dim);
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.8rem;
  animation: bounce 2s ease-in-out infinite;
}

.scroll-arrow {
  width: 20px;
  height: 20px;
  border-right: 2px solid var(--terminal-green);
  border-bottom: 2px solid var(--terminal-green);
  transform: rotate(45deg);
  margin: 10px auto 0;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(10px); }
}


/* Section Titles */
.section-title {
  font-family: 'JetBrains Mono', monospace;
  font-size: 1.8rem;
  color: var(--terminal-text);
  margin-bottom: 40px;
}

.title-prefix {
  color: var(--terminal-green);
  margin-right: 10px;
}

.section-subtitle {
  color: var(--terminal-text-dim);
  margin-bottom: 30px;
  font-size: 1rem;
}


/* About Section */
.about-section {
  padding: 80px 0;
}

.image-frame {
  position: relative;
  max-width: 350px;
  margin: 0 auto;
}

.portrait-img {
  width: 100%;
  border-radius: 8px;
  filter: grayscale(20%);
  transition: filter 0.3s ease;
}

.portrait-img:hover {
  filter: grayscale(0%);
}

.image-border {
  position: absolute;
  top: 15px;
  left: 15px;
  width: 100%;
  height: 100%;
  border: 2px solid var(--terminal-green);
  border-radius: 8px;
  z-index: -1;
  transition: all 0.3s ease;
}

.image-frame:hover .image-border {
  top: 10px;
  left: 10px;
}

.terminal-card {
  background: var(--terminal-card-bg);
  border: 1px solid var(--terminal-border);
  border-radius: 8px;
  overflow: hidden;
}

.terminal-card-header {
  background: var(--terminal-bg);
  padding: 10px 15px;
  border-bottom: 1px solid var(--terminal-border);
  display: flex;
  align-items: center;
  gap: 8px;
}

.terminal-card-title {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.8rem;
  color: var(--terminal-text-dim);
  margin-left: 10px;
}

.terminal-card-body {
  padding: 20px;
}

.info-grid {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.9rem;
}

.info-item {
  margin-bottom: 12px;
  line-height: 1.5;
}

.info-key {
  color: var(--terminal-cyan);
}

.info-value {
  color: var(--terminal-amber);
}


/* Skills Section */
.skills-section {
  padding: 80px 0;
}

.skill-item {
  margin-bottom: 20px;
}

.skill-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.9rem;
}

.skill-name {
  color: var(--terminal-text);
}

.skill-level {
  color: var(--terminal-green);
}

.skill-bar {
  height: 8px;
  background: var(--terminal-bg);
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid var(--terminal-border);
}

.skill-progress {
  height: 100%;
  background: linear-gradient(90deg, var(--terminal-green-dim), var(--terminal-green));
  border-radius: 4px;
  transition: width 1s ease-out;
}

.skill-bar.fun .skill-progress {
  background: linear-gradient(90deg, var(--terminal-amber), #ff6b00);
}

.language-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid var(--terminal-border);
  font-family: 'JetBrains Mono', monospace;
}

.language-item:last-child {
  border-bottom: none;
}

.lang-name {
  color: var(--terminal-text);
}


/* Tech Section */
.tech-section {
  padding: 80px 0;
}

.tech-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 30px;
}

.tech-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 20px;
  background: var(--terminal-bg);
  border: 1px solid var(--terminal-border);
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.tech-item:hover,
.tech-item.bloop-hover,
.tech-item.bloop-press {
  border-color: var(--terminal-green);
  box-shadow: 0 0 20px rgba(57, 255, 20, 0.2);
  transform: translateY(-5px);
}

.tech-icon {
  width: 60px;
  height: 60px;
  object-fit: contain;
  filter: grayscale(30%);
  transition: filter 0.3s ease;
}

.tech-item:hover .tech-icon,
.tech-item.bloop-hover .tech-icon,
.tech-item.bloop-press .tech-icon {
  filter: grayscale(0%);
}

.skill-item.bloop-hover .skill-name,
.language-item.bloop-hover .lang-name,
.info-value [data-bloop-kind="hobby"].bloop-hover,
.section-title.bloop-hover {
  color: var(--terminal-green);
}

.info-item.bloop-hover .info-value {
  text-shadow: 0 0 8px var(--terminal-amber);
}

.tech-name {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.8rem;
  color: var(--terminal-text-dim);
}


/* Git Section */
.git-section {
  padding: 80px 0;
}

.git-comment {
  font-family: 'JetBrains Mono', monospace;
  color: var(--terminal-green);
  margin-bottom: 20px;
}

.git-image {
  max-width: 100%;
  border-radius: 8px;
  margin-bottom: 20px;
}

.git-footnote {
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.8rem;
  color: var(--terminal-text-dim);
  font-style: italic;
}


/* Responsive */
@media (max-width: 768px) {
  .terminal-window {
    margin: 0 20px;
  }

  .greeting-output {
    font-size: 1.5rem;
  }

  .section-title {
    font-size: 1.4rem;
  }

  .info-grid {
    font-size: 0.8rem;
  }
}
</style>
